<template>
    <meta name="csrf-token" content="{{ csrf_token() }}" />
    <div class="flex gap-3">
        <h1
            class="mb-3 flex flex-row flex-wrap content-center text-sm font-bold text-gray-400"
        >
            <span class="mt-2 flex w-full items-center"> DOCUMENTOS</span>
        </h1>
        <template v-if="user.idwall_customer_uuid">
            <button
                :disabled="loading"
                type="button"
                @click="viewKyc()"
                class="flex items-center rounded-md bg-blue-500 px-4 py-2 font-medium text-white"
            >
                Visualizar KYC
            </button>
            <button
                :disabled="loading || syncing"
                type="button"
                @click="syncIdwallStatus()"
                class="flex items-center rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:bg-gray-300 disabled:cursor-not-allowed px-4 py-2 font-medium text-white"
            >
                <svg v-if="syncing" class="mr-2 h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                Sincronizar status com IdWall
            </button>
            <span
                v-if="idwallStatusLoading"
                class="inline-flex items-center self-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300"
            >
                <svg class="mr-1.5 h-3 w-3 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                Status IDWall: Carregando...
            </span>
            <span
                v-else-if="idwallStatusError"
                class="inline-flex items-center self-center rounded-full bg-red-100 px-2.5 py-1 text-xs font-medium text-red-800 dark:bg-red-900 dark:text-red-200"
            >
                Status IDWall: Falha na busca
            </span>
            <span
                v-else-if="idwallStatus"
                :class="idwallStatusBadgeClass(idwallStatus)"
                class="inline-flex items-center self-center rounded-full px-2.5 py-1 text-xs font-medium"
            >
                Status IDWall: {{ idwallStatusLabel(idwallStatus) }}
            </span>
        </template>
        <span
            v-if="idwallUnlock && idwallUnlock.current_screen"
            :class="idwallScreenBadgeClass(idwallUnlock.current_screen)"
            :title="idwallUnlock.current_screen.label"
            class="inline-flex items-center self-center rounded-full px-2.5 py-1 text-xs font-medium"
        >
            Fluxo IdWall: {{ idwallUnlock.current_screen.short }}
        </span>
        <button
            v-if="idwallUnlock && idwallUnlock.can_unlock"
            :disabled="unlocking"
            :title="idwallUnlock.message"
            type="button"
            @click="unlockIdwall()"
            class="flex items-center rounded-md bg-indigo-500 hover:bg-indigo-400 disabled:cursor-not-allowed disabled:bg-gray-300 px-4 py-2 font-medium text-white"
        >
            <svg v-if="unlocking" class="mr-2 h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            Desbloquear IdWall
        </button>
    </div>

    <empty-state
        title="Nenhum documento pessoal encontrado"
        message="Este usuário ainda não possui documentos registrados."
        v-if="!loading && documents && documents.length === 0"
    />
    <div class="flex flex-col gap-4">
        <div
            class="flex items-center justify-between rounded-md p-4 shadow dark:bg-gray-800"
            v-for="document in documents"
            v-bind:key="document.id"
        >
            <div class="flex items-center gap-4">
                <div
                    class="flex h-8 w-8 items-center justify-center rounded-md dark:text-white"
                >
                    <DocumentIcon class="h-5 w-5 shrink-0 text-gray-400" />
                </div>
                <p class="font-medium dark:text-white">
                    {{ formatDocumentName(document.collection_name) }}
                </p>
            </div>
            <div class="flex items-center gap-2">
                <div
                    v-if="document.status == 'approved'"
                    class="mr-4 flex items-center justify-center rounded-lg bg-white p-4 shadow-md"
                >
                    <div class="mr-10 flex items-center justify-center">
                        <div
                            class="flex h-10 w-10 items-center justify-center rounded-full bg-green-500"
                        >
                            <CheckIcon
                                class="h-5 w-5 shrink-0 text-gray-400 text-gray-900 dark:text-white"
                            />
                        </div>
                        <div class="ml-3">
                            <h3 class="text-lg font-bold text-gray-800">
                                Validado
                            </h3>
                            <p
                                class="text-gray-600"
                                v-if="
                                    shouldShowDocumentType(
                                        document.collection_name,
                                    )
                                "
                            >
                                Tipo:
                                {{
                                    (
                                        document.document_type ??
                                        "Não Informado"
                                    ).toUpperCase()
                                }}
                            </p>
                            <p class="text-gray-600" v-if="document.document_type == 'cnh'">
                                Número: {{ document.document_number ?? "Não Informado" }}
                            </p>
                            <p class="text-gray-600">
                                Por: {{ document.reviewed_by?.name ?? '' }}
                            </p>
                            <p class="text-gray-600">
                                Em: {{ formatDate(document.reviewed_at) }}
                            </p>
                        </div>
                    </div>
                </div>

                <div
                    v-if="
                        document.status == 'pending' &&
                        shouldShowDocumentType(document.collection_name)
                    "
                    class="mr-10 flex items-center rounded-full border border-white bg-white/10 px-4 py-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                    <DocumentIcon
                        class="mr-2 h-5 w-5 shrink-0 text-gray-900 dark:text-white"
                    />
                    <span>
                        Tipo:
                        {{
                            (
                                document.document_type ?? "Não Informado"
                            ).toUpperCase()
                        }}
                        <br>
                        <span v-if="document.document_type == 'cnh'">
                            Número: {{ document.document_number ?? "Não Informado" }}
                        </span>
                    </span>
                </div>

                <div
                    v-if="document.status == 'pending'"
                    class="mr-10 flex items-center rounded-full border border-yellow-300 bg-yellow-100 px-4 py-2 text-sm font-medium text-yellow-700"
                >
                    <ExclamationCircleIcon
                        class="mr-2 h-5 w-5 shrink-0 text-yellow-700"
                    />
                    <span>Pendente Aprovação</span>
                </div>

                <div
                    v-if="document.status == 'reproved'"
                    class="mr-4 flex items-center justify-center rounded-lg bg-white p-4 shadow-md"
                >
                    <div class="mr-10 flex items-center justify-center">
                        <div
                            class="flex h-10 w-10 items-center justify-center rounded-full bg-red-500"
                        >
                            <ExclamationTriangleIcon
                                class="h-5 w-5 shrink-0 text-gray-400 text-gray-900 dark:text-white"
                            />
                        </div>
                        <div class="ml-3">
                            <h3 class="text-lg font-bold text-gray-800">
                                Reprovado
                            </h3>
                            <p
                                class="text-gray-600"
                                v-if="
                                    shouldShowDocumentType(
                                        document.collection_name,
                                    )
                                "
                            >
                                Tipo:
                                {{
                                    (
                                        document.document_type ??
                                        "Não Informado"
                                    ).toUpperCase()
                                }}
                            </p>
                            <p class="text-gray-600" v-if="document.document_type == 'cnh'">
                                Número: {{ document.document_number ?? "Não Informado" }}
                            </p>
                            <p class="text-gray-600">
                                Por: {{ document.reviewed_by?.name ?? '' }}
                            </p>
                            <p class="text-gray-600">
                                Em: {{ formatDate(document.reviewed_at) }}
                            </p>
                        </div>
                    </div>
                </div>

                <button
                    @click="showImage(document.temporary_url)"
                    class="flex items-center rounded-md bg-blue-500 px-4 py-2 font-medium text-white"
                >
                    <PhotoIcon class="mr-2 h-5 w-5 text-gray-400" />
                    Visualizar
                </button>

                <button
                    @click="openModal(document)"
                    class="flex items-center rounded-md bg-yellow-500 px-4 py-2 font-medium text-white"
                >
                    <PencilSquareIcon
                        class="h-5 w-5 shrink-0 text-gray-400 text-gray-900 dark:text-white"
                    />
                    Editar
                </button>
                <button
                    v-if="
                        document.status != 'approved'
                    "
                    @click="updateStatus(document.id, 'approved')"
                    class="flex items-center rounded-md bg-green-500 px-4 py-2 font-medium text-white"
                >
                    <CheckBadgeIcon
                        class="h-5 w-5 shrink-0 text-gray-400 text-gray-900 dark:text-white"
                    />
                    Validar
                </button>
                <button
                    v-if="
                        document.status !== 'reproved' &&
                        document.status !== 'approved'
                    "
                    @click="updateStatus(document.id, 'reproved')"
                    class="flex items-center rounded-md bg-red-500 px-4 py-2 font-medium text-white"
                >
                    <ExclamationTriangleIcon
                        class="h-5 w-5 shrink-0 text-gray-400 text-gray-900 dark:text-white"
                    />
                    Reprovar
                </button>
                <button
                    @click="deleteDocument(document.id)"
                    class="flex items-center rounded-md bg-red-500 px-4 py-2 font-medium text-white"
                >
                    <TrashIcon
                        class="h-5 w-5 shrink-0 text-gray-400 text-gray-900 dark:text-white"
                    />
                    Deletar
                </button>
            </div>
        </div>
    </div>

    <div class="mt-4 mb-4 flex justify-center">
        <button
            @click="fetchDeletedHistory()"
            class="flex items-center rounded-md bg-gray-600 px-4 py-2 font-medium text-white hover:bg-gray-700"
        >
            <TrashIcon class="mr-2 h-5 w-5 text-gray-600 dark:text-gray-300" />
            Documentos Excluídos
        </button>
    </div>

    <h1
        class="mb-3 flex flex-row flex-wrap content-center text-sm font-bold text-gray-400"
    >
        <span class="mt-2 flex w-full items-center">
            DOCUMENTOS CONTA BANCÁRIA
        </span>
    </h1>
    <empty-state
        title="Nenhum documento bancário encontrado"
        message="Este usuário ainda não cadastrou nenhuma conta bancária."
        v-if="
            !loading &&
            documentsBankAccount &&
            documentsBankAccount.length === 0
        "
    />
    <div class="flex flex-col gap-4">
        <div
            class="flex items-center justify-between rounded-md p-4 shadow dark:bg-gray-800"
            v-for="document in documentsBankAccount"
            v-bind:key="document.id"
        >
            <div class="flex items-center gap-4">
                <div
                    class="flex h-8 w-8 items-center justify-center rounded-md dark:text-white"
                >
                    <DocumentIcon class="h-5 w-5 shrink-0 text-gray-400" />
                </div>
                <p
                    class="font-medium dark:text-white"
                    v-if="shouldShowDocumentName(document.collection_name)"
                >
                    {{ formatDocumentName(document.collection_name) }}
                </p>
            </div>
            <div class="flex items-center gap-2">
                <div
                    v-if="document.status == 'approved'"
                    class="mr-4 flex items-center justify-center rounded-lg bg-white p-4 shadow-md"
                >
                    <div class="mr-10 flex items-center justify-center">
                        <div
                            class="flex h-10 w-10 items-center justify-center rounded-full bg-green-500"
                        >
                            <CheckIcon
                                class="h-5 w-5 shrink-0 text-gray-400 text-gray-900 dark:text-white"
                            />
                        </div>
                        <div class="ml-3">
                            <h3 class="text-lg font-bold text-gray-800">
                                Validado
                            </h3>
                            <p
                                class="text-gray-600"
                                v-if="
                                    shouldShowDocumentType(
                                        document.collection_name,
                                    )
                                "
                            >
                                Tipo:
                                {{
                                    (
                                        document.document_type ??
                                        "Não Informado"
                                    ).toUpperCase()
                                }}
                            </p>
                            <p class="text-gray-600" v-if="document.document_type == 'cnh'">
                                Número: {{ document.document_number ?? "Não Informado" }}
                            </p>
                            <p class="text-gray-600">
                                Por: {{ document.reviewed_by?.name ?? '' }}
                            </p>
                            <p class="text-gray-600">
                                Em: {{ formatDate(document.reviewed_at) }}
                            </p>
                        </div>
                    </div>
                </div>

                <div
                    v-if="document.status == 'pending'"
                    class="mr-10 flex items-center rounded-full border border-white bg-white/10 px-4 py-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                    <DocumentIcon
                        class="mr-2 h-5 w-5 shrink-0 text-gray-900 dark:text-white"
                    />
                    <span
                        v-if="shouldShowDocumentType(document.collection_name)"
                    >
                        Tipo:
                        {{
                            (
                                document.document_type ?? "Não Informado"
                            ).toUpperCase()
                        }}
                        <br>
                        <span v-if="document.document_type == 'cnh'">
                            Número: {{ document.document_number ?? "Não Informado" }}
                        </span>
                    </span>
                </div>

                <div
                    v-if="document.status == 'pending'"
                    class="mr-10 flex items-center rounded-full border border-yellow-300 bg-yellow-100 px-4 py-2 text-sm font-medium text-yellow-700"
                >
                    <ExclamationCircleIcon
                        class="mr-2 h-5 w-5 shrink-0 text-yellow-700"
                    />
                    <span>Pendente Aprovação</span>
                </div>

                <div
                    v-if="document.status == 'reproved'"
                    class="mr-4 flex items-center justify-center rounded-lg bg-white p-4 shadow-md"
                >
                    <div class="mr-10 flex items-center justify-center">
                        <div
                            class="flex h-10 w-10 items-center justify-center rounded-full bg-red-500"
                        >
                            <ExclamationTriangleIcon
                                class="h-5 w-5 shrink-0 text-gray-400 text-gray-900 dark:text-white"
                            />
                        </div>
                        <div class="ml-3">
                            <h3 class="text-lg font-bold text-gray-800">
                                Reprovado
                            </h3>
                            <p
                                class="text-gray-600"
                                v-if="
                                    shouldShowDocumentType(
                                        document.collection_name,
                                    )
                                "
                            >
                                Tipo:
                                {{
                                    (
                                        document.document_type ??
                                        "Não Informado"
                                    ).toUpperCase()
                                }}
                            </p>
                            <p class="text-gray-600" v-if="document.document_type == 'cnh'">
                                Número: {{ document.document_number ?? "Não Informado" }}
                            </p>
                            <p class="text-gray-600">
                                Por: {{ document.reviewed_by?.name ?? '' }}
                            </p>
                            <p class="text-gray-600">
                                Em: {{ formatDate(document.reviewed_at) }}
                            </p>
                        </div>
                    </div>
                </div>

                <button
                    @click="showImage(document.temporary_url)"
                    class="flex items-center rounded-md bg-blue-500 px-4 py-2 font-medium text-white"
                >
                    <PhotoIcon class="mr-2 h-5 w-5 text-gray-400" />
                    Visualizar
                </button>
            </div>
        </div>
    </div>

    <modal class="4xl" :open="show" @close="show = false">
        <div
            class="flex min-h-fit w-full flex-col flex-wrap space-y-3 divide-y divide-slate-500 text-gray-900 dark:text-white"
        ></div>
        <h1
            class="mb-2 flex flex-row flex-wrap content-center items-center border-b border-slate-500 pb-1 text-lg text-sm font-bold text-gray-900 dark:text-white"
        >
            Alterar Documento -
            {{ formatDocumentName(this.document.collection_name) }}
        </h1>

        <input
            class="block w-full cursor-pointer rounded-lg border border-gray-300 bg-gray-50 text-sm text-gray-900 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400 dark:placeholder-gray-400"
            id="file_input"
            type="file"
            ref="file"
        />

        <label
            for="type"
            class="mt-4 block text-sm font-medium text-gray-700 dark:text-white"
        >
            Tipo de Documento:
        </label>
        <select
            v-model="this.document.document_type"
            id="document_type"
            class="text-input"
        >
            <option value="cnh">CNH</option>
            <option value="rg">RG</option>
        </select>

        <div v-if="this.document.document_type == 'cnh'">
            <label
                for="document_number"
                class="mt-4 block text-sm font-medium text-gray-700 dark:text-white"
            >
                Número do Documento:
            </label>
            <input
                v-model="this.document.document_number"
                id="document_number"
                class="text-input"
            />
        </div>

        <div class="mt-3 flex justify-center">
            <button
                :disabled="loading"
                type="button"
                @click.prevent="sendDocument"
                :loading="loading"
                class="mb-3 inline-flex w-full max-w-xs justify-center rounded-md border border-transparent bg-yellow-500 px-4 py-2 text-base font-medium text-white shadow-sm focus:outline-none sm:w-auto sm:text-sm"
            >
                <PencilSquareIcon
                    class="h-5 w-5 shrink-0 text-gray-400 text-gray-900 dark:text-white"
                />
                Editar
            </button>
        </div>
    </modal>

    <modal class="4xl" :open="decisionModal" @close="decisionModal = false">
        <div
            class="flex min-h-fit w-full flex-col flex-wrap space-y-3 divide-y divide-slate-500 text-gray-900 dark:text-white"
        ></div>
        <h1
            class="mb-2 flex flex-row flex-wrap content-center items-center border-b border-slate-500 pb-1 text-lg text-sm font-bold text-gray-900 dark:text-white"
        >
            Validação
        </h1>

        <div v-if="decision != 'approved'">
            <p class="rounded-md bg-blue-600 p-4 text-white shadow-md">
                Caso deseje, você pode informar um motivo no campo abaixo. Se
                preferir não preencher, será utilizada uma mensagem padrão para
                o usuário.
            </p>

            <ReasonSelect
                reasonType="user"
                @reason-selected="handleReasonSelected"
            />
        </div>

        <p class="mt-4 text-gray-900 dark:text-white">
            Você tem certeza que deseja
            {{ decision == "approved" ? "validar" : "reprovar" }} este
            documento?
        </p>

        <div class="mt-3 flex justify-center space-x-3">
            <button
                :disabled="loading"
                type="button"
                @click.prevent="updateStatusFetch(decisionDocumment, decision)"
                :loading="loading"
                class="mb-3 inline-flex w-full max-w-xs justify-center rounded-md border border-transparent bg-green-500 px-4 py-2 text-base font-medium text-white shadow-sm focus:outline-none sm:w-auto sm:text-sm"
            >
                <CheckIcon
                    class="h-5 w-5 shrink-0 text-gray-400 text-gray-900 dark:text-white"
                />
                Sim
            </button>
            <button
                type="button"
                @click.prevent="decisionModal = false"
                class="mb-3 inline-flex w-full max-w-xs justify-center rounded-md border border-transparent bg-red-500 px-4 py-2 text-base font-medium text-white shadow-sm focus:outline-none sm:w-auto sm:text-sm"
            >
                <ExclamationTriangleIcon
                    class="h-5 w-5 shrink-0 text-gray-400 text-gray-900 dark:text-white"
                />
                Não
            </button>
        </div>
    </modal>
    <!-- Modal de motivo para exclusão -->
    <modal class="4xl" :open="deleteReasonModal" @close="deleteReasonModal = false">
        <div
            class="flex min-h-fit w-full flex-col flex-wrap space-y-3 divide-y divide-slate-500 text-gray-900 dark:text-white"
        ></div>
        <h1
            class="mb-2 flex flex-row flex-wrap content-center items-center border-b border-slate-500 pb-1 text-lg text-sm font-bold text-gray-900 dark:text-white"
        >
            Excluir Documento
        </h1>

        <p class="mt-2 text-gray-900 dark:text-white">
            Informe o motivo da exclusão deste documento:
        </p>

        <textarea
            v-model="deleteReason"
            rows="3"
            class="mt-2 block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
            placeholder="Motivo da exclusão..."
        ></textarea>

        <div class="mt-3 flex justify-center space-x-3">
            <button
                :disabled="loading || !deleteReason"
                type="button"
                @click.prevent="confirmDeleteDocument()"
                class="mb-3 inline-flex w-full max-w-xs justify-center rounded-md border border-transparent bg-red-500 px-4 py-2 text-base font-medium text-white shadow-sm focus:outline-none sm:w-auto sm:text-sm disabled:opacity-50"
            >
                <TrashIcon class="mr-1 h-5 w-5 shrink-0 text-gray-900 dark:text-white" />
                Confirmar Exclusão
            </button>
            <button
                type="button"
                @click.prevent="deleteReasonModal = false"
                class="mb-3 inline-flex w-full max-w-xs justify-center rounded-md border border-transparent bg-gray-500 px-4 py-2 text-base font-medium text-gray-900 dark:text-white shadow-sm focus:outline-none sm:w-auto sm:text-sm"
            >
                Cancelar
            </button>
        </div>
    </modal>

    <!-- Modal de histórico de documentos excluídos -->
    <modal class="4xl" size="4xl" :open="deletedHistoryModal" @close="deletedHistoryModal = false">
        <div
            class="flex min-h-fit w-full flex-col space-y-3 text-gray-900 dark:text-white"
        >
            <h1
                class="mb-2 border-b border-slate-500 pb-1 text-lg font-bold text-gray-900 dark:text-white"
            >
                Documentos Excluídos
            </h1>

            <div v-if="deletedHistoryLoading" class="flex items-center justify-center py-8">
                <svg class="h-6 w-6 animate-spin text-gray-900 dark:text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                <span class="ml-2">Carregando...</span>
            </div>

            <div v-else-if="deletedHistory.length === 0" class="py-8 text-center text-gray-400">
                Nenhum documento excluído encontrado.
            </div>

            <div v-else class="max-h-96 overflow-y-auto">
                <table class="w-full text-left text-sm">
                    <thead class="border-b border-slate-600 text-gray-400">
                        <tr>
                            <th class="px-3 py-2">Documento</th>
                            <th class="px-3 py-2">Arquivo</th>
                            <th class="px-3 py-2">Excluído por</th>
                            <th class="px-3 py-2">Data</th>
                            <th class="px-3 py-2">Motivo</th>
                            <th class="px-3 py-2">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="item in deletedHistory"
                            :key="item.id"
                            class="border-b border-slate-700"
                        >
                            <td class="px-3 py-2">{{ formatDocumentName(item.collection_name) }}</td>
                            <td class="px-3 py-2 text-xs text-gray-400">{{ item.file_name }}</td>
                            <td class="px-3 py-2">{{ item.deleted_by_user?.name ?? 'N/A' }}</td>
                            <td class="px-3 py-2">{{ formatDate(item.created_at) }}</td>
                            <td class="px-3 py-2">{{ item.reason }}</td>
                            <td class="px-3 py-2">
                                <button
                                    v-if="item.temporary_url"
                                    @click="showImage(item.temporary_url)"
                                    class="flex items-center rounded-md bg-blue-500 px-3 py-1 text-xs font-medium text-white hover:bg-blue-600"
                                >
                                    <PhotoIcon class="mr-1 h-4 w-4" />
                                    Visualizar
                                </button>
                                <span v-else class="text-xs text-gray-500">Indisponível</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="mt-3 flex justify-center">
                <button
                    type="button"
                    @click.prevent="deletedHistoryModal = false"
                    class="mb-3 inline-flex w-full max-w-xs justify-center rounded-md border border-transparent bg-yellow-500 px-4 py-2 text-base font-medium text-white shadow-sm focus:outline-none sm:w-auto sm:text-sm"
                >
                    Fechar
                </button>
            </div>
        </div>
    </modal>

    <vue-easy-lightbox
        :visible="vueEasy"
        :imgs="urlDocument"
        :index="0"
        @hide="handleHide"
    />
    <modal
        class="min-h-full"
        size="4xl"
        :open="showDocumentModal"
        @close="showDocumentModal = false"
    >
        <div
            class="flex min-h-full w-full flex-col space-y-3 divide-y divide-slate-500 text-gray-900 dark:text-white"
            style="height: 1000px"
        >
            <h1
                class="mb-2 border-b border-slate-500 pb-1 text-lg font-bold text-gray-900 dark:text-white"
            >
                Documento
            </h1>

            <div class="flex h-full grow items-stretch">
                <iframe
                    :src="urlDocument"
                    class="h-full w-full"
                    frameborder="0"
                    allowfullscreen
                ></iframe>
            </div>

            <div class="mt-5 flex justify-center space-x-3">
                <button
                    type="button"
                    @click.prevent="showDocumentModal = false"
                    class="mb-3 inline-flex w-full max-w-xs justify-center rounded-md border border-transparent bg-yellow-500 px-4 py-2 text-base font-medium text-white shadow-sm focus:outline-none sm:w-auto sm:text-sm"
                >
                    Fechar
                </button>
            </div>
        </div>
    </modal>
</template>

<script>
import {
    ArrowDownCircleIcon,
    CheckBadgeIcon,
    CheckIcon,
    DocumentIcon,
    ExclamationCircleIcon,
    ExclamationTriangleIcon,
    PaperClipIcon,
    PencilSquareIcon,
    PhotoIcon,
    TrashIcon,
} from "@heroicons/vue/24/outline";
import dayjs from "dayjs";
import VueEasyLightbox from "vue-easy-lightbox";
import { useToast } from "vue-toastification";
import ReasonSelect from "../../components/layouts/ReasonSelect.vue";
import HelperMixin from "../../mixins/HelperMixin.vue";
import EmptyState from "../layouts/EmptyState.vue";
import Modal from "../layouts/Modal.vue";
import UpdateDocuments from "./UpdateDocuments.vue";

export default {
    components: {
        UpdateDocuments,
        PaperClipIcon,
        EmptyState,
        ArrowDownCircleIcon,
        CheckBadgeIcon,
        CheckIcon,
        PencilSquareIcon,
        DocumentIcon,
        ExclamationCircleIcon,
        DocumentIcon,
        ExclamationTriangleIcon,
        TrashIcon,
        Modal,
        PhotoIcon,
        VueEasyLightbox,
        ReasonSelect,
    },
    props: {
        user: { type: Object, required: true },
        statuses: { String, required: true },
    },
    mixins: [HelperMixin],
    setup() {
        const toast = useToast();
        return { toast };
    },
    data() {
        return {
            loading: false,
            documentsBankAccount: [],
            documents: [],
            show: false,
            decisionModal: false,
            decision: null,
            image_url: null,
            document: null,
            decisionDocumment: null,
            showDocumentModal: false,
            urlDocument: null,
            vueEasy: false,
            reason: null,
            deleteReasonModal: false,
            deleteReason: null,
            deleteDocumentId: null,
            deletedHistoryModal: false,
            deletedHistoryLoading: false,
            deletedHistory: [],
            lastKycProcessExternalId: null,
            idwallStatus: null,
            idwallStatusLoading: false,
            idwallStatusError: false,
            syncing: false,
            idwallUnlock: null,
            unlocking: false,
        };
    },

    mounted() {
        this.loadConfig();
        this.fetchKyc();
        this.fetch(false);
        this.loadIdwallUnlock();
        console.info("user");
        console.info(this.user);
    },

    methods: {
        async showImage(index) {
            const response = await fetch(index, { method: "GET" });
            const contentType = response.headers.get("Content-Type");

            if (contentType.includes("image")) {
                this.index = index;
                this.urlDocument = index;
                this.vueEasy = true;
            } else {
                this.urlDocument = index;
                this.showDocumentModal = true;
            }
        },
        loadConfig() {
            axios
                .get("/api/kyc/config")
                .then(({ data }) => {
                    this.aegisUrl = data.aegis_url?.replace("api/v1", "") || "";
                })
                .catch((error) => {
                    console.error("Error loading KYC config:", error);
                });
        },
        syncIdwallStatus() {
            if (this.syncing) return;

            const confirmed = window.confirm(
                'Essa ação verifica os webhooks da IdWall já recebidos e aprova os documentos / atualiza o status do usuário com base no que está aprovado lá.\n\nSó use quando o usuário passou pelo fluxo IdWall mas o status no Smaug está dessincronizado.\n\nDeseja continuar?'
            );
            if (!confirmed) return;

            this.syncing = true;
            axios.post(`/api/kyc/biometric-revalidation/${this.user.id}/sync-idwall-status`)
                .then(({ data }) => {
                    const actions = (data?.data?.actions || []).join('\n - ');
                    const message = actions
                        ? `${data.message}\n\nAções aplicadas:\n - ${actions}`
                        : data.message;
                    this.toast.success(message, { timeout: 8000 });
                    this.loadDocuments?.();
                    this.loadIdwallStatus?.();
                })
                .catch((error) => {
                    const message = error?.response?.data?.message ?? 'Erro ao sincronizar status com IdWall.';
                    this.toast.error(message);
                })
                .finally(() => {
                    this.syncing = false;
                });
        },
        idwallScreenBadgeClass(screen) {
            if (screen.is_stuck) {
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            }

            return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
        },
        loadIdwallUnlock() {
            axios.get(`/api/kyc/biometric-revalidation/${this.user.id}/idwall-unlock`)
                .then(({ data }) => {
                    this.idwallUnlock = data?.data ?? null;
                })
                .catch(() => {
                    this.idwallUnlock = null;
                });
        },
        unlockIdwall() {
            if (this.unlocking || !this.idwallUnlock) return;

            const details = (this.idwallUnlock.details || []).join('\n');
            const confirmed = window.confirm(
                `Essa ação coloca o usuário no fluxo da IdWall pelo caminho que o painel dele realmente consegue exibir.\n\nResultado esperado:\n${this.idwallUnlock.message}${details ? `\n\n${details}` : ''}\n\nDeseja continuar?`
            );
            if (!confirmed) return;

            this.unlocking = true;
            axios.post(`/api/kyc/biometric-revalidation/${this.user.id}/idwall-unlock`)
                .then(({ data }) => {
                    const actions = (data?.data?.actions || []).join('\n - ');
                    const message = actions
                        ? `${data.message}\n\nAções aplicadas:\n - ${actions}`
                        : data.message;
                    this.toast.success(message, { timeout: 12000 });
                    this.fetch(false);
                    this.loadIdwallUnlock();
                })
                .catch((error) => {
                    const message = error?.response?.data?.message ?? 'Erro ao desbloquear o fluxo da IdWall.';
                    this.toast.error(message);
                })
                .finally(() => {
                    this.unlocking = false;
                });
        },
        viewKyc() {
            // If idwall_customer_uuid exists, use it to view the IDWall profile
            if (this.user.idwall_customer_uuid) {
                window.open(
                    `https://app.idwall.co/profile/?id=${this.user.idwall_customer_uuid}`,
                    "_blank"
                );
                return;
            }
            
            // Otherwise use the regular KYC URL
            this.redirectExternalKycUrl(
                this.lastKycProcessExternalId,
                this.user.type,
                this.user.cnpj
            );
        },
        
        redirectExternalKycUrl(externalId, userType, cnpj) {
            if (userType === "company" && cnpj) {
                window.open(
                    `${this.aegisUrl}company-report/${cnpj}/kyc-provider-page`,
                    "_blank",
                );
                return;
            }

            window.open(
                `${this.aegisUrl}kyc-process/${externalId}/customer-kyc-provider-page`,
                "_blank"
            );
        },
        handleHide() {
            this.vueEasy = false;
        },
        openModal(document) {
            this.show = true;
            this.document = document;
        },
        formatDate(date) {
            return dayjs(date).format("DD/MM/YYYY HH:mm:ss");
        },
        fetchStatus() {
            axios
                .get("/api/user/" + this.user.id + "/status")
                .then(({ data }) => {
                    this.$emit("update-statuses", data);
                    this.loading = false;
                })
                .catch((error) => {
                    this.loading = false;
                    this.toast.error(error.response.data.message);
                });
        },
        updateStatus(document, decision) {
            this.decisionModal = true;
            this.decision = decision;
            this.decisionDocumment = document;
        },
        updateStatusFetch(document, decision) {
            const route =
                "/api/user/" +
                this.user.id +
                "/documents/" +
                document +
                "/status";
            axios
                .put(route, { decision: decision, reason: this.reason })
                .then(({ data }) => {
                    this.toast.success("Salvo com sucesso.");
                    this.fetch(false);
                    this.fetchStatus();
                    this.show = false;
                    this.decisionModal = false;
                    this.decision = null;
                    this.decisionDocumment = null;
                })
                .catch((error) => {});
        },
        handleReasonSelected(reason) {
            this.reason = reason;
        },
        fetchKyc() {
            this.loading = true;
            this.idwallStatusLoading = true;
            this.idwallStatusError = false;

            const params = {};
            if (this.user?.cpf) {
                params["filter[text]"] = this.user.cpf;
            }

            axios
                .get("/api/kyc/users", { params })
                .then(({ data }) => {
                    if (Array.isArray(data.data) && data.data.length > 0) {
                        const firstUser = data.data[0];
                        this.lastKycProcessExternalId = firstUser.last_kyc_process_external_id;
                        this.idwallStatus = firstUser.idwall_status ?? null;
                    }
                    this.loading = false;
                    this.idwallStatusLoading = false;
                })
                .catch((error) => {
                    this.loading = false;
                    this.idwallStatusLoading = false;
                    this.idwallStatusError = true;
                    this.toast.error("Erro ao buscar dados do KYC.");
                });
        },

        fetch(update = true) {
            this.documents = [];
            this.documentsBankAccount = [];
            this.loading = true;

            if (update) {
                this.documents = [];
            }

            axios
                .get("/api/user/" + this.user.id + "/documents")
                .then(({ data }) => {
                    this.documents = [...data, ...this.documents];
                })
                .catch((error) => {});

            if (this.user.bank_accounts.at(-1)?.id !== undefined) {
                axios
                    .get(
                        "/api/bank-account/" +
                            this.user.bank_accounts.at(-1)?.id +
                            "/documents",
                    )
                    .then(({ data }) => {
                        this.documentsBankAccount = [
                            ...data,
                            ...this.documentsBankAccount,
                        ];
                    })
                    .catch((error) => {});
            }

            this.loading = false;
        },
        openLink(url) {
            this.showDocumentModal = true;
            this.urlDocument = url;
            //window.open(url, "_blank");
        },

        sendDocument() {
            if (!this.$refs.file.files[0]) {
                const route =
                    "/api/user/" +
                    this.user.id +
                    "/documents/" +
                    this.document.id +
                    "/type";
                axios
                    .put(
                        route,
                        {
                            type: this.document.document_type,
                            document_number:
                                this.document.document_number ?? null,
                        },
                        {
                            headers: {
                                "X-CSRF-TOKEN": document
                                    .querySelector('meta[name="csrf-token"]')
                                    .getAttribute("content"),
                            },
                        },
                    )
                    .then(({ data }) => {
                        this.toast.success("Salvo com sucesso.");
                        this.fetch(false);
                        this.fetchStatus();
                        this.show = false;
                        this.decisionModal = false;
                        this.decision = null;
                        this.decisionDocumment = null;
                    })
                    .catch((error) => {
                        this.toast.error("Erro ao atualizar documento!");
                    });
                return;
            }

            this.loading = true;

            Vapor.store(this.$refs.file.files[0], {
                progress: (progress) => {
                    this.progress = Math.round(progress * 100);
                },
            }).then((response) => {
                axios
                    .put(
                        "/api/user/" + this.user.id + "/documents",
                        {
                            uuid: response.uuid,
                            key: response.key,
                            bucket: response.bucket,
                            name: this.$refs.file.files[0].name,
                            content_type: this.$refs.file.files[0].type,
                            collection: this.document.collection_name,
                            type: this.document.document_type,
                            document_number:
                                this.document.document_number ?? null,
                        },
                        {
                            headers: {
                                "X-CSRF-TOKEN": document
                                    .querySelector('meta[name="csrf-token"]')
                                    .getAttribute("content"),
                            },
                        },
                    )
                    .then((response) => {
                        this.toast.success("Documento atualizado com sucesso!");
                    })
                    .catch((error) => {
                        this.toast.error("Erro ao atualizar documento!");
                    })
                    .finally(() => {
                        this.loading = false;
                        this.update = false;
                        this.fetch(false);
                        this.show = false;
                    });
            });
        },

        deleteDocument(documentId) {
            this.deleteDocumentId = documentId;
            this.deleteReason = null;
            this.deleteReasonModal = true;
        },
        confirmDeleteDocument() {
            this.loading = true;
            axios
                .delete("/api/user/" + this.user.id + "/documents/" + this.deleteDocumentId, {
                    data: { reason: this.deleteReason },
                })
                .then((response) => {
                    this.toast.success("Documento deletado com sucesso!");
                    this.fetch(false);
                    this.fetchStatus();
                    this.deleteReasonModal = false;
                    this.deleteReason = null;
                    this.deleteDocumentId = null;
                })
                .catch((error) => {
                    this.toast.error("Erro ao deletar documento!");
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        fetchDeletedHistory() {
            this.deletedHistoryModal = true;
            this.deletedHistoryLoading = true;
            this.deletedHistory = [];

            axios
                .get("/api/user/" + this.user.id + "/documents/deleted-history")
                .then(({ data }) => {
                    this.deletedHistory = data;
                })
                .catch((error) => {
                    this.toast.error("Erro ao buscar histórico de documentos excluídos!");
                })
                .finally(() => {
                    this.deletedHistoryLoading = false;
                });
        },
        idwallStatusLabel(status) {
            return {
                'approved':                'Aprovado',
                'finished':                'Aprovado',
                'pending':                 'Pendente',
                'in_progress':             'Em análise',
                'waiting_manual_approval': 'Aguardando revisão manual',
                'waiting_manual_action':   'Aguardando ação manual',
                'inconclusive':            'Inconclusivo',
                'rejected':                'Rejeitado',
                'cancelled':               'Cancelado',
                'invalid':                 'Inválido',
            }[status] ?? status;
        },
        idwallStatusBadgeClass(status) {
            return {
                'approved':                'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
                'finished':                'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
                'pending':                 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
                'in_progress':             'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
                'waiting_manual_approval': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
                'waiting_manual_action':   'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
                'inconclusive':            'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
                'rejected':                'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
                'cancelled':               'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
                'invalid':                 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
            }[status] ?? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
        },
        shouldShowDocumentType(collectionName) {
            const documentsWithType = [
                "documentFront",
                "document_front",
                "documentBack",
                "document_back",
                "selfiePhoto",
                "selfie_photo",
                "bankDocument",
                "bank_document",
            ];
            return documentsWithType.includes(collectionName);
        },
        shouldShowDocumentName(collectionName) {
            const documentsWithName = [
                "documentFront",
                "document_front",
                "documentBack",
                "document_back",
                "selfiePhoto",
                "selfie_photo",
                "bankDocument",
                "bank_document",
            ];
            return documentsWithName.includes(collectionName);
        },
    },
};
</script>

<style>
.image-gallery {
    display: flex;
    gap: 10px;
}

.thumbnail {
    width: 100px;
    height: auto;
    cursor: pointer;
    transition: transform 0.2s;
}

.thumbnail:hover {
    transform: scale(1.1);
}
</style>
